import React from 'react';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useTenantQuery } from '../hooks/useTenantQuery';

interface TenantQueryWrapperProps {
  children: React.ReactNode;
}

const TenantQueryWrapper: React.FC<TenantQueryWrapperProps> = ({
  children,
}) => {
  const {
    tenantError,
    isLoadingTenant,
    clearTenantError,
    removeTenantIdFromUrl,
  } = useTenantQuery();

  // Show loading spinner when switching tenants
  if (isLoadingTenant) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md mx-auto">
          <ProgressSpinner
            style={{ width: '3rem', height: '3rem' }}
            strokeWidth="4"
            className="mb-4"
          />
          <h3 className="text-2xl font-semibold text-gray-800 mb-3">
            Switching Organization...
          </h3>
          <p className="text-gray-600 text-lg">
            Please wait while we load your organization.
          </p>
        </div>
      </div>
    );
  }

  // Show error message for tenant permission issues
  if (tenantError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
        style={{ minHeight: '100vh' }}
      >
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="pi pi-ban text-red-500 text-4xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-3">
              Access Denied
            </h2>
            <p className="text-gray-600 text-lg">
              You don&apos;t have permission to access this organization.
            </p>
          </div>

          <Message
            severity="error"
            text={tenantError}
            className="w-full mb-6 text-lg"
            style={{ fontSize: '1.125rem' }}
          />

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              label="Go Back"
              icon="pi pi-arrow-left"
              onClick={() => {
                clearTenantError();
                removeTenantIdFromUrl();
                window.history.back();
              }}
              severity="secondary"
              outlined
              className="flex-1 p-3 text-lg"
            />
            <Button
              label="Select Organization"
              icon="pi pi-building"
              onClick={() => {
                clearTenantError();
                removeTenantIdFromUrl();
                // Navigate to tenant selection or dashboard
                window.location.href = '/';
              }}
              className="flex-1 p-3 text-lg"
            />
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default TenantQueryWrapper;
